import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSwitchLanguage, type SupportedLang } from '@/lib/i18n-routing';

const languages = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'ARB' },
  { code: 'hi', label: 'हिन्दी', short: 'HI' },
  { code: 'ru', label: 'Русский', short: 'RU' },
];

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile';
  inverted?: boolean;
}

const LanguageSelector = ({ variant = 'desktop', inverted = false }: LanguageSelectorProps) => {
  const { i18n } = useTranslation();
  const currentLanguage = languages.find((l) => l.code === i18n.language) || languages[0];
  const switchLanguage = useSwitchLanguage();
  const [open, setOpen] = useState(false);

  const changeLanguage = (code: string) => {
    switchLanguage(code as SupportedLang);
    setOpen(false);
  };

  if (variant === 'mobile') {
    return (
      <div className="px-4 py-3 border-b border-border">
        <p className="text-xs font-bold text-nav-foreground/50 tracking-wider mb-2">
          LANGUAGE
        </p>
        <div className="flex gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              aria-label={`Switch language to ${lang.label}`}
              aria-current={i18n.language === lang.code ? 'true' : undefined}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                i18n.language === lang.code
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'bg-muted text-nav-foreground/70 hover:bg-muted/80'
              }`}
            >
              <span>{lang.short}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        className={`flex items-center gap-1.5 px-2 transition-colors ${
          inverted
            ? 'text-white/80 hover:bg-white/10 hover:text-white'
            : 'text-nav-foreground/80 hover:bg-nav-foreground/5 hover:text-nav-foreground'
        }`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Change language, current: ${currentLanguage.label}`}
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-medium">{currentLanguage.short}</span>
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-40 rounded-lg border border-border bg-background p-1 shadow-lg" role="menu">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            aria-label={`Switch language to ${lang.label}`}
            aria-current={i18n.language === lang.code ? 'true' : undefined}
            className="flex w-full items-center justify-between rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted"
            role="menuitem"
          >
            <div className="flex items-center gap-2">
              <span className="font-medium text-nav-foreground/60">{lang.short}</span>
              <span>{lang.label}</span>
            </div>
            {i18n.language === lang.code && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </button>
        ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
